import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Autocomplete,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Skeleton,
  Switch,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  ArrowForward as NextIcon,
  Preview as PreviewIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Category as CategoryIcon,
  SubdirectoryArrowRight as SubcategoryIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
  Visibility as VisibleIcon,
  VisibilityOff as HiddenIcon,
  Crop as CropIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Cancel as CancelIcon,
  Upload as UploadIcon,
  Link as LinkIcon,
  PhotoCamera as CameraIcon,
  FlipCameraIos as FlipCameraIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ChromePicker } from 'react-color';
import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { CategoryService } from '../services/categoryService';
import { SubcategoryService } from '../services/subcategoryService';
import { UserProductService, UserProduct } from '../services/userProductService';
import { ProductDetailService, ProductDetail } from '../services/productDetailService';
import { Category, Subcategory } from '../lib/supabase';
import { DeliveryOptionsService, DeliveryOption } from '../services/deliveryOptionsService';
import { supabase } from '../lib/supabase';
import ImageGallery from '../components/ImageGallery/ImageGallery';
import UserDocumentsModal from '../components/Documents/UserDocumentsModal';
import { PlatformSettingsService, PlatformFeeSettings } from '../services/platformSettingsService';
import { FilterSettingsService } from '../services/filterSettingsService';
import { JOB_SECONDARY_LINKS } from '../components/Layout/JobSecondaryNavBar';
import { jobMegaMenuData } from '../components/Layout/jobMegaMenuData';
import { 
  calculatePriceWithFeesSync, 
  calculateBasePriceFromCustomerPrice,
  calculatePlatformFeeSync 
} from '../utils/priceUtils';
import { useTranslation } from '../contexts/TranslationContext';

interface ProductFormData {
  name: string;
  description: string;
  price: number;
  originalPrice?: number;
  category: string;
  subcategory: string;
  industries: string[];
  images: string[];
  colors: string[];
  customColors: string[];
  sizeSystem: string;
  sizes: string[];
  condition: 'new' | 'like_new' | 'good' | 'fair' | 'poor';
  material?: string;
  brand?: string;
  model?: string;
  sku?: string;
  weight?: number;
  dimensions?: { length: number; width: number; height: number };
  tags: string[];
  showSalary?: boolean;
  salaryType?: 'hourly' | 'fixed' | 'negotiable';
  location?: string;
  jobType?: 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship' | 'temporary' | 'volunteer';
  experienceLevel?: 'entry-level' | 'mid-level' | 'senior-level' | 'executive';
  duration?: string;
  remoteWorkAllowed?: boolean;
  requiredSkills?: string[];
  jobRequirements?: string[];
  benefitsPerks?: string[];
  contactEmail?: string;
  applicationDeadline?: string;
  expectedStartDate?: string;
}

const normalizeToIsoString = (value: any): string => {
  if (!value) return '';
  try {
    if (typeof value === 'string') {
      const v = value.trim();
      if (!v) return '';
      const d = dayjs(v);
      if (!d.isValid()) return '';
      return d.toISOString();
    }
    const d = dayjs(value);
    if (!d.isValid()) return '';
    return d.toISOString();
  } catch {
    return '';
  }
};

const EditProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const isEditJobRoute = useMemo(() => location.pathname.startsWith('/edit-job/'), [location.pathname]);
  
  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const restoreDoneRef = useRef(false);
  const [skipProductFetch, setSkipProductFetch] = useState(false);

  // Storage keys per product
  const formKey = useMemo(() => (id ? `editProduct:${id}:form:v1` : ''), [id]);
  const stepKey = useMemo(() => (id ? `editProduct:${id}:step:v1` : ''), [id]);
  const shippingKey = useMemo(() => (id ? `editProduct:${id}:shipping:v1` : ''), [id]);
  
  // Categories and subcategories
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [subcategoriesError, setSubcategoriesError] = useState<string | null>(null);
  
  // Color picker state
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [currentCustomColor, setCurrentCustomColor] = useState('#ff0000');
  
  // Cropping state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [imgRef, setImgRef] = useState<HTMLImageElement | null>(null);
  const imageProcessedRef = useRef(false);
  
  // Gallery state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  
  // Documents modal state
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  
  // Image upload state
  const [imageInput, setImageInput] = useState('');
  const [fileInputRef, setFileInputRef] = useState<HTMLInputElement | null>(null);
  
  // Platform fee settings and pricing mode
  const [platformFeeSettings, setPlatformFeeSettings] = useState<PlatformFeeSettings | null>(null);
  const [pricingMode, setPricingMode] = useState<'base' | 'customer'>('base'); // 'base' = set base price, 'customer' = set customer price
  
  // Camera capture state
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment'); // 'user' = front, 'environment' = back

  // Backblaze state
  const [b2Items, setB2Items] = useState<Array<{ key: string; size: number; lastModified: string; url?: string }>>([]);
  const [b2NextToken, setB2NextToken] = useState<string | null>(null);
  const [b2Loading, setB2Loading] = useState<boolean>(false);
  const [b2Enabled, setB2Enabled] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProductFormData>({
    defaultValues: {
      name: '',
      description: '',
      price: 0,
      originalPrice: undefined,
      category: '',
      subcategory: '',
      industries: [],
      images: [],
      colors: [],
      customColors: [],
      sizeSystem: '',
      sizes: [],
      condition: 'new',
      material: '',
      brand: '',
      model: '',
      sku: '',
      weight: undefined,
      dimensions: { length: 0, width: 0, height: 0 },
      tags: [],
      showSalary: true,
      salaryType: 'hourly',
      location: '',
      jobType: 'full-time',
      experienceLevel: 'entry-level',
      duration: '',
      remoteWorkAllowed: false,
      requiredSkills: [],
      jobRequirements: [],
      benefitsPerks: [],
      contactEmail: '',
      applicationDeadline: '',
      expectedStartDate: '',
    },
  });

  const watchedCategory = watch('category');
  const watchedImages = watch('images');
  const watchedCustomColors = watch('customColors');
  const watchedSizeSystem = watch('sizeSystem');
  const watchedPrice = watch('price');
  const watchedIndustries = watch('industries');
  const watchedShowSalary = watch('showSalary');

  // Size systems
  const sizeSystems: { [key: string]: string[] } = {
    'clothing-men': ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
    'clothing-women': ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'],
    'shoes': ['35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46'],
    'electronics': ['One Size'],
    'books': ['Paperback', 'Hardcover', 'E-book'],
    'jewelry': ['One Size'],
    'home': ['Small', 'Medium', 'Large', 'Extra Large'],
  };

  // State to store loaded product data
  const [loadedProduct, setLoadedProduct] = useState<ProductDetail | null>(null);
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const [selectedDeliveryOptionIds, setSelectedDeliveryOptionIds] = useState<string[]>([]);
  const [deliveryOptionsLoading, setDeliveryOptionsLoading] = useState(false);
  const [deliveryOptionsError, setDeliveryOptionsError] = useState<string | null>(null);

  const isEditingJob = useMemo(() => {
    if (isEditJobRoute) return true;
    return Boolean((loadedProduct as any)?.isJob || (loadedProduct as any)?.is_job);
  }, [isEditJobRoute, loadedProduct]);

  const [industriesDialogOpen, setIndustriesDialogOpen] = useState(false);
  const [availableIndustries, setAvailableIndustries] = useState<string[]>(() => JOB_SECONDARY_LINKS.map((l) => l.label));
  const [industriesEnabled, setIndustriesEnabled] = useState<Record<string, boolean>>({});
  const [subcategoriesEnabled, setSubcategoriesEnabled] = useState<Record<string, Record<string, boolean>>>({});
  const [activeIndustryInDialog, setActiveIndustryInDialog] = useState<string | null>(null);

  useEffect(() => {
    const loadEnabled = async () => {
      try {
        const res = await fetch('/website-settings.json', { cache: 'no-store' });
        if (!res.ok) return;
        const json = await res.json();
        const enabled: Record<string, boolean> = json?.categoriesEnabled || {};
        setIndustriesEnabled(enabled);
        setSubcategoriesEnabled(json?.subcategoriesEnabled || {});
      } catch {
        // ignore
      }
    };
    loadEnabled();
  }, []);

  useEffect(() => {
    const all = JOB_SECONDARY_LINKS.map((l) => l.label);
    const filtered = all.filter((label) => {
      if (Object.prototype.hasOwnProperty.call(industriesEnabled, label)) {
        return !!industriesEnabled[label];
      }
      return true;
    });
    setAvailableIndustries(filtered);

    setActiveIndustryInDialog((prev) => {
      if (prev && filtered.includes(prev)) return prev;
      return filtered.length > 0 ? filtered[0] : null;
    });
  }, [industriesEnabled]);

  const isSubEnabled = (top: string, label: string): boolean => {
    const m = subcategoriesEnabled?.[top];
    if (!m || typeof m[label] === 'undefined') return true;
    return !!m[label];
  };

  const getSubcategoryPathsForIndustry = (top: string): string[] => {
    const content: any = (jobMegaMenuData as any)[top] || (jobMegaMenuData as any).default;
    const left: Array<{ key: string; label: string }> = Array.isArray(content?.left) ? content.left : [];
    const rightByKey: Record<string, any[]> = content?.rightByKey || {};

    const paths: string[] = [];
    for (const leftItem of left) {
      const groupLabel = String(leftItem?.label || '').trim();
      const isAllGroup = leftItem?.key === 'all' || groupLabel.toLowerCase() === 'alle anzeigen' || groupLabel.toLowerCase() === 'all roles';
      const cols: any[] = Array.isArray(rightByKey[leftItem.key]) ? rightByKey[leftItem.key] : [];
      for (const col of cols) {
        const items: any[] = Array.isArray(col?.items) ? col.items : [];
        for (const it of items) {
          const lbl = typeof it === 'string' ? it : String(it?.label || '').trim();
          if (!lbl) continue;
          if (lbl.toLowerCase() === 'alle anzeigen' || lbl.toLowerCase() === 'all roles') continue;
          if (!isSubEnabled(top, lbl)) continue;
          const path = isAllGroup ? `${top} / ${lbl}` : `${top} / ${groupLabel} / ${lbl}`;
          paths.push(path);
        }
      }
    }

    return Array.from(new Set(paths));
  };

  const renderIndustriesDialog = () => {
    const selected = new Set(watchedIndustries || []);
    const activeTop = activeIndustryInDialog;
    const subPaths = activeTop ? getSubcategoryPathsForIndustry(activeTop) : [];

    const getSelectedCountForTop = (top: string) => {
      const all = getSubcategoryPathsForIndustry(top);
      let cnt = 0;
      for (const p of all) if (selected.has(p)) cnt++;
      return { total: all.length, selected: cnt };
    };

    return (
      <Dialog
        open={industriesDialogOpen}
        onClose={() => setIndustriesDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Kategorien auswählen</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1.25fr' }, minHeight: 320 }}>
            <Box sx={{ borderRight: { sm: '1px solid' }, borderColor: 'divider' }}>
              <List disablePadding>
                {availableIndustries.map((label) => {
                  const counts = getSelectedCountForTop(label);
                  const hasAny = counts.selected > 0;
                  const isAll = counts.total > 0 && counts.selected === counts.total;
                  const indeterminate = counts.selected > 0 && counts.selected < counts.total;
                  const isActive = activeIndustryInDialog === label;
                  return (
                    <ListItem key={label} disablePadding>
                      <ListItemButton
                        onClick={() => setActiveIndustryInDialog(label)}
                        sx={{ py: 1.1, px: 2, ...(isActive && { bgcolor: 'action.hover' }) }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            checked={isAll || (counts.total === 0 && selected.has(label))}
                            indeterminate={indeterminate}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              const next = new Set(selected);
                              const allPaths = getSubcategoryPathsForIndustry(label);

                              if (allPaths.length > 0) {
                                const isFullySelected = allPaths.every((p) => next.has(p));
                                if (isFullySelected) allPaths.forEach((p) => next.delete(p));
                                else allPaths.forEach((p) => next.add(p));
                                next.delete(label);
                              } else {
                                if (next.has(label)) next.delete(label);
                                else next.add(label);
                              }
                              setValue('industries', Array.from(next));
                            }}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={label}
                          primaryTypographyProps={{ fontWeight: 800 }}
                          secondary={hasAny ? `${counts.selected}${counts.total ? ` / ${counts.total}` : ''} ausgewählt` : undefined}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>

            <Box sx={{ p: 0 }}>
              <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  {activeTop || 'Kategorie'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Wähle passende Unterkategorien
                </Typography>
              </Box>
              <List disablePadding>
                {activeTop && subPaths.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary="Keine Unterkategorien verfügbar"
                      secondary="Diese Kategorie kann nur als Hauptkategorie ausgewählt werden."
                    />
                  </ListItem>
                )}
                {activeTop && subPaths.map((path) => {
                  const parts = path.split(' / ');
                  const leaf = parts[parts.length - 1];
                  const checked = selected.has(path);
                  return (
                    <ListItem key={path} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          const next = new Set(selected);
                          if (next.has(path)) next.delete(path);
                          else next.add(path);
                          if (activeTop) next.delete(activeTop);
                          setValue('industries', Array.from(next));
                        }}
                        sx={{ py: 1.1, px: 2 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText primary={leaf} primaryTypographyProps={{ fontWeight: 700 }} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setValue('industries', [])}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
          >
            Zurücksetzen
          </Button>
          <Button
            variant="contained"
            onClick={() => setIndustriesDialogOpen(false)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
          >
            Fertig
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const steps = useMemo(() => {
    if (isEditingJob) {
      return ['Grundinformationen', 'Details', 'Überprüfen & Veröffentlichen'];
    }
    return [t('editProduct.steps.basic'), t('editProduct.steps.detailsImages'), t('editProduct.steps.shipping'), t('editProduct.steps.reviewPublish')];
  }, [isEditingJob, t]);

  // Handle back navigation intelligently
  const handleBackNavigation = () => {
    // Check if we have state from the previous page
    if (location.state?.fromProductDetail) {
      // Navigate back to the product detail page
      navigate(`/product/${id}`, { 
        state: { 
          preserveFilters: true,
          fromEdit: true 
        }
      });
    } else if (location.state?.fromDashboard) {
      // Navigate back to dashboard
      navigate('/dashboard');
    } else {
      // Default: go back in history or to dashboard
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/dashboard');
      }
    }
  };

  // Restore from localStorage first; if present, skip product fetch
  useEffect(() => {
    if (!id || restoreDoneRef.current) return;
    restoreDoneRef.current = true;
    let restored = false;
    try {
      if (formKey) {
        const savedForm = localStorage.getItem(formKey);
        if (savedForm) {
          const parsed = JSON.parse(savedForm);
          Object.entries(parsed || {}).forEach(([key, value]) => {
            // @ts-ignore
            setValue(key, value);
          });
          restored = true;
        }
      }
      if (stepKey) {
        const savedStep = localStorage.getItem(stepKey);
        if (savedStep !== null) setActiveStep(Number(savedStep) || 0);
      }
      if (shippingKey) {
        const savedShip = localStorage.getItem(shippingKey);
        if (savedShip) setSelectedDeliveryOptionIds(JSON.parse(savedShip));
      }
    } catch {}
    if (restored) {
      // We have a full form snapshot; skip fetching product and stop loading state here
      setSkipProductFetch(true);
      setLoading(false);
    }
  }, [id, formKey, stepKey, shippingKey, setValue]);

  // Load product data (only when not restored)
  useEffect(() => {
    const loadProductData = async () => {
      if (!id) {
        setError(t('editProduct.errors.productIdRequired'));
        setLoading(false);
        return;
      }

      if (skipProductFetch) return; // state restored; skip fetch

      try {
        setLoading(true);
        setError(null);
        
        console.log('EditProductPage - Loading product with ID:', id);
        
        // Check if product is cached before fetching
        const isCached = ProductDetailService.isProductCached(id);
        if (isCached) {
          console.log('EditProductPage - Product is cached, loading instantly');
        }
        
        // Check authentication first
        const { data: { user } } = await supabase.auth.getUser();
        console.log('EditProductPage - Current user:', user);
        
        if (!user) {
          setError(t('editProduct.errors.mustBeLoggedIn'));
          setLoading(false);
          return;
        }
        
        const product = await ProductDetailService.getProductByIdForEdit(id);
        
        console.log('EditProductPage - Fetched product for edit:', product);
        
        if (!product) {
          setError(t('editProduct.errors.notFound'));
          setLoading(false);
          return;
        }

        // Check if user can edit this product (must be the seller)
        if (product.sellerId !== user.id) {
          console.log('EditProductPage - Permission denied. Product seller:', product.sellerId, 'Current user:', user.id);
          setError(t('editProduct.errors.permissionDenied'));
          setLoading(false);
          return;
        }

        console.log('EditProductPage - Product loaded successfully:', product);
        setLoadedProduct(product);
        // Prefill shipping selections from product if available
        if ((product as any).delivery_option_ids) {
          setSelectedDeliveryOptionIds((product as any).delivery_option_ids);
        }
        setLoading(false);
      } catch (err) {
        console.error('EditProductPage - Error loading product:', err);
        setError(t('editProduct.errors.failedToLoad'));
        setLoading(false);
      }
    };

    loadProductData();
  }, [id, skipProductFetch]);

  // Load website settings to detect Backblaze usage and prefetch user folder images
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch('/website-settings.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const enabled = data?.uploadTarget === 'backblaze';
        setB2Enabled(!!enabled);
      } catch {}
    };
    loadSettings();
  }, []);

  const fetchMyBackblazeImages = async (mode: 'reset' | 'next' = 'reset') => {
    if (isEditingJob) return;
    if (!b2Enabled) return;
    if (mode === 'next' && !b2NextToken) return;
    setB2Loading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { setB2Loading(false); return; }
      const payload: any = { signRead: true, maxKeys: 100 };
      if (mode === 'next' && b2NextToken) payload.continuationToken = b2NextToken;
      const res = await fetch('/api/b2-list.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) throw new Error(json?.error || raw || 'Failed');
      const items = Array.isArray(json.items) ? json.items : [];
      const base: string | null = typeof json.publicBaseUrl === 'string' ? json.publicBaseUrl : null;
      const mapped = items.map((it: any) => {
        const preferred = it.url && typeof it.url === 'string' && it.url.length > 0
          ? it.url
          : (base ? `${base.replace(/\/$/, '')}/${encodeURIComponent(it.key).replace(/%2F/g,'/')}` : undefined);
        return { ...it, url: preferred };
      });
      setB2Items((prev) => mode === 'reset' ? mapped : [...prev, ...mapped]);
      setB2NextToken(json.nextContinuationToken || null);
      // Merge into images form field for preview
      const urls: string[] = mapped.map((it: any) => it.url).filter(Boolean);
      if (urls.length > 0) {
        const currImgs: string[] = (watch('images') || []) as string[];
        const merged = Array.from(new Set([...(currImgs || []), ...urls]));
        setValue('images', merged);
      }
    } catch {
    } finally {
      setB2Loading(false);
    }
  };

  // Auto-load B2 images when enabled
  useEffect(() => {
    if (!isEditingJob && b2Enabled) fetchMyBackblazeImages('reset');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b2Enabled]);

  // Load platform fee settings on mount
  useEffect(() => {
    const loadFeeSettings = async () => {
      try {
        const settings = await PlatformSettingsService.getPlatformFeeSettings();
        setPlatformFeeSettings(settings);
      } catch (error) {
        console.error('Error loading platform fee settings:', error);
        // Set default fee settings if loading fails
        setPlatformFeeSettings({
          fee_type: 'fixed',
          fee_amount: 0.50,
          fee_percentage: null,
        });
      }
    };
    loadFeeSettings();
  }, []);

  // Populate form when product data is loaded (and categories for non-job products)
  useEffect(() => {
    if (loadedProduct && (isEditingJob || categories.length > 0)) {
      // Populate form with existing data
      setValue('name', loadedProduct.title);
      setValue('description', loadedProduct.description);
      setValue('price', loadedProduct.price);
      setValue('originalPrice', loadedProduct.originalPrice);

      if (Boolean((loadedProduct as any)?.isJob || (loadedProduct as any)?.is_job)) {
        const paths: string[] = Array.isArray((loadedProduct as any)?.categories) ? (loadedProduct as any).categories : [];
        setValue('industries', paths);
      }
      
      // Find category and subcategory IDs by name (products only)
      if (!isEditingJob) {
        const categoryObj = categories.find(c => c.name === loadedProduct.category);
        setValue('category', categoryObj?.id || '');
      }

      if (isEditingJob) {
        setValue('salaryType', (loadedProduct as any).salaryType || (loadedProduct as any).salary_type || undefined);
        setValue('location', (loadedProduct as any).location || '');
        setValue('jobType', (loadedProduct as any).jobType || (loadedProduct as any).job_type || undefined);
        setValue('experienceLevel', (loadedProduct as any).experienceLevel || (loadedProduct as any).experience_level || undefined);
        setValue('duration', (loadedProduct as any).duration || '');
        setValue('remoteWorkAllowed', Boolean((loadedProduct as any).remoteWorkAllowed ?? (loadedProduct as any).remote_work_allowed ?? false));
        setValue('requiredSkills', Array.isArray((loadedProduct as any).requiredSkills) ? (loadedProduct as any).requiredSkills : (Array.isArray((loadedProduct as any).required_skills) ? (loadedProduct as any).required_skills : []));
        setValue('jobRequirements', Array.isArray((loadedProduct as any).jobRequirements) ? (loadedProduct as any).jobRequirements : (Array.isArray((loadedProduct as any).job_requirements) ? (loadedProduct as any).job_requirements : []));
        setValue('benefitsPerks', Array.isArray((loadedProduct as any).benefitsPerks) ? (loadedProduct as any).benefitsPerks : (Array.isArray((loadedProduct as any).benefits_perks) ? (loadedProduct as any).benefits_perks : []));
        setValue('contactEmail', (loadedProduct as any).contactEmail || (loadedProduct as any).contact_email || '');
        setValue(
          'applicationDeadline',
          normalizeToIsoString((loadedProduct as any).applicationDeadline || (loadedProduct as any).application_deadline)
        );
        setValue(
          'expectedStartDate',
          normalizeToIsoString((loadedProduct as any).expectedStartDate || (loadedProduct as any).expected_start_date)
        );
        const st = ((loadedProduct as any).salaryType || (loadedProduct as any).salary_type || '') as string;
        const p = Number(loadedProduct.price || 0);
        setValue('showSalary', !(String(st) === 'negotiable' || p <= 0));
        // Keep legacy mappings in sync
        setValue('brand', (loadedProduct as any).company?.name || loadedProduct.brand || '');
        setValue('model', (loadedProduct as any).jobType || loadedProduct.model || '');
        setValue('sku', (loadedProduct as any).salaryType || loadedProduct.sku || '');
        setValue('tags', loadedProduct.tags);
      } else {
        setValue('images', loadedProduct.images);
        setValue('colors', loadedProduct.color || []);
        setValue('customColors', loadedProduct.customColors || []);
        setValue('sizeSystem', loadedProduct.sizeSystem || '');
        setValue('sizes', loadedProduct.size);
        setValue('condition', loadedProduct.condition);
        setValue('material', loadedProduct.material || '');
        setValue('brand', loadedProduct.brand || '');
        setValue('model', loadedProduct.model || '');
        setValue('sku', loadedProduct.sku || '');
        setValue('weight', loadedProduct.weight);
        setValue('dimensions', loadedProduct.dimensions || { length: 0, width: 0, height: 0 });
        setValue('tags', loadedProduct.tags);
      }
    }
  }, [loadedProduct, categories, isEditingJob, setValue]);

  // Populate subcategory when category changes and subcategories are loaded
  useEffect(() => {
    if (isEditingJob) return;
    if (loadedProduct && subcategories.length > 0 && watchedCategory) {
      const subcategoryObj = subcategories.find(s => s.name === loadedProduct.subcategory);
      setValue('subcategory', subcategoryObj?.id || '');
    }
  }, [isEditingJob, loadedProduct, subcategories, watchedCategory, setValue]);

  // Reset cropping state when modal closes
  useEffect(() => {
    if (!cropModalOpen) {
      setCropImage(null);
      setCrop(undefined);
      setCompletedCrop(undefined);
      setScale(1);
      setRotate(0);
      setImgRef(null);
      imageProcessedRef.current = false;
    }
  }, [cropModalOpen]);

  // Load categories
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      setCategoriesError(null);
      try {
        const data = await CategoryService.getProductCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategoriesError(t('editProduct.categories.loadingFailed'));
      } finally {
        setLoadingCategories(false);
      }
    };

    if (!isEditingJob) {
      fetchCategories();
    }
  }, []);

  // Load subcategories when category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!watchedCategory) {
        setSubcategories([]);
        setValue('subcategory', '');
        return;
      }

      setLoadingSubcategories(true);
      setSubcategoriesError(null);
      try {
        const data = await SubcategoryService.getProductSubcategoriesByCategory(watchedCategory);
        setSubcategories(data);
        setValue('subcategory', ''); // Reset subcategory when category changes
      } catch (error) {
        console.error('Error fetching subcategories:', error);
        setSubcategoriesError(t('editProduct.subcategories.loadingFailed'));
      } finally {
        setLoadingSubcategories(false);
      }
    };

    fetchSubcategories();
  }, [watchedCategory, setValue]);

  // Persist step
  useEffect(() => {
    try { if (stepKey) localStorage.setItem(stepKey, String(activeStep)); } catch {}
  }, [activeStep, stepKey]);

  // Persist shipping selections
  useEffect(() => {
    try { if (shippingKey) localStorage.setItem(shippingKey, JSON.stringify(selectedDeliveryOptionIds)); } catch {}
  }, [selectedDeliveryOptionIds, shippingKey]);

  // Persist form on any change
  useEffect(() => {
    const sub = watch((value) => {
      try { if (formKey) localStorage.setItem(formKey, JSON.stringify(value)); } catch {}
    });
    return () => sub.unsubscribe();
  }, [watch, formKey]);

  // Load delivery options
  useEffect(() => {
    if (isEditingJob) return;
    const load = async () => {
      try {
        setDeliveryOptionsLoading(true);
        setDeliveryOptionsError(null);
        const options = await DeliveryOptionsService.getAll('DE');
        setDeliveryOptions(options);
      } catch (e: any) {
        setDeliveryOptionsError(e?.message || t('editProduct.delivery.loadingFailed'));
      } finally {
        setDeliveryOptionsLoading(false);
      }
    };
    load();
  }, [isEditingJob, t]);

  // Handle form submission
  const onSubmit = async (data: ProductFormData) => {
    // Validation
    if (!data.name || data.name.length < 3) {
      toast.error(isEditingJob ? 'Bitte gib einen Jobtitel mit mindestens 3 Zeichen an.' : t('editProduct.validation.nameMin'));
      return;
    }

    if (!data.description || data.description.length < 10) {
      toast.error(isEditingJob ? 'Bitte gib eine Beschreibung mit mindestens 10 Zeichen an.' : t('editProduct.validation.descMin'));
      return;
    }

    if (!isEditingJob) {
      if (!data.price || data.price <= 0) {
        toast.error(t('editProduct.validation.priceValid'));
        return;
      }
    } else {
      if ((data.showSalary ?? true) && (!data.price || data.price <= 0)) {
        toast.error('Bitte gib ein Gehalt an oder deaktiviere die Anzeige.');
        return;
      }
    }

    if (isEditingJob) {
      if (!Array.isArray(data.industries) || data.industries.length === 0) {
        toast.error('Bitte wähle mindestens eine Kategorie.');
        return;
      }
    } else {
      if (!data.category) {
        toast.error(t('editProduct.validation.categoryRequired'));
        return;
      }
      if (!data.subcategory) {
        toast.error(t('editProduct.validation.subcategoryRequired'));
        return;
      }
    }

    if (!isEditingJob) {
      if ((!data.colors || data.colors.length === 0) && (!data.customColors || data.customColors.length === 0)) {
        toast.error(t('editProduct.validation.colorRequired'));
        return;
      }

      if (!data.sizeSystem) {
        toast.error(t('editProduct.validation.sizeSystemRequired'));
        return;
      }

      if (!data.sizes || data.sizes.length === 0) {
        toast.error(t('editProduct.validation.sizeRequired'));
        return;
      }

      if (!data.images || data.images.length === 0) {
        toast.error(t('editProduct.validation.imageRequired'));
        return;
      }
    } else {
      if ((data.showSalary ?? true) && !data.salaryType) {
        toast.error('Bitte wähle einen Gehaltstyp.');
        return;
      }
      if (!data.jobType) {
        toast.error('Bitte wähle einen Jobtyp.');
        return;
      }
      if (!data.experienceLevel) {
        toast.error('Bitte wähle ein Erfahrungslevel.');
        return;
      }
    }

    // Check content filters before proceeding
    try {
      const filterChecks = await FilterSettingsService.checkMultipleContentViolations([
        { type: 'name', value: data.name },
        { type: 'description', value: data.description },
        { type: 'brand', value: data.brand || '' },
        { type: 'tags', value: (data.tags || []).join(' ') },
      ]);

      if (filterChecks.violated) {
        // Find blocking filters
        const blockingFilters = filterChecks.violations
          .flatMap(v => v.violations)
          .filter(f => f.action_type === 'block');

        if (blockingFilters.length > 0) {
          const blockedFields = filterChecks.violations
            .filter(v => v.violations.some(f => f.action_type === 'block'))
            .map(v => v.type);
          
          const fields = blockedFields.join(', ');
          toast.error(
            t('editProduct.validation.contentBlocked').replace('{fields}', fields),
            { duration: 6000 }
          );
          return;
        }

        // If only flagging filters matched, show warning but allow
        const flaggedFields = filterChecks.violations.map(v => v.type).join(', ');
        toast(
          t('editProduct.validation.contentFlagged').replace('{fields}', flaggedFields),
          { 
            duration: 5000,
            icon: '⚠️',
            style: {
              background: '#ff9800',
              color: '#fff',
            },
          }
        );
      }
    } catch (error) {
      console.error('Error checking content filters:', error);
      // Continue with upload if filter check fails (fail open)
    }

    setIsUploading(true);
    try {
      // Convert price to base price if user was in customer mode
      let finalPrice = data.price;
      let finalOriginalPrice = data.originalPrice;
      
      if (platformFeeSettings && pricingMode === 'customer') {
        // Convert customer price to base price
        finalPrice = calculateBasePriceFromCustomerPrice(data.price, platformFeeSettings);
        if (finalPrice <= 0) {
          toast.error(t('editProduct.validation.priceTooLowForFees'));
          setIsUploading(false);
          return;
        }
        // Also convert original price if provided
        if (data.originalPrice) {
          finalOriginalPrice = calculateBasePriceFromCustomerPrice(data.originalPrice, platformFeeSettings);
          if (finalOriginalPrice <= 0) {
            finalOriginalPrice = undefined; // Invalid original price, remove it
          }
        }
      }
      
      // Update product in Supabase
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error(t('editProduct.validation.mustBeLoggedInToUpdate'));
        return;
      }

      const updatePayload: any = {
        name: data.name,
        description: data.description,
        price: finalPrice,
        original_price: finalOriginalPrice || null,
        is_active: true,
        product_status: 'ACTIVE',
        brand: data.brand || null,
        tags: data.tags || [],
        updated_at: new Date().toISOString(),
      };

      if (isEditingJob) {
        updatePayload.categories = data.industries || [];
        updatePayload.salary_type = (data.showSalary ?? true) ? (data.salaryType || null) : 'negotiable';
        updatePayload.location = data.location || null;
        updatePayload.job_type = data.jobType || null;
        updatePayload.experience_level = data.experienceLevel || null;
        updatePayload.duration = data.duration || null;
        updatePayload.remote_work_allowed = data.remoteWorkAllowed ?? false;
        updatePayload.required_skills = data.requiredSkills || [];
        updatePayload.job_requirements = data.jobRequirements || [];
        updatePayload.benefits_perks = data.benefitsPerks || [];
        updatePayload.contact_email = data.contactEmail || null;
        updatePayload.application_deadline = data.applicationDeadline || null;
        updatePayload.expected_start_date = data.expectedStartDate || null;

        // Legacy fields kept for backwards compatibility
        updatePayload.model = data.jobType || data.model || null;
        updatePayload.sku = (data.showSalary ?? true) ? (data.salaryType || data.sku || null) : ('negotiable');
      } else {
        updatePayload.images = data.images;
        updatePayload.colors = data.colors;
        updatePayload.custom_colors = data.customColors;
        updatePayload.size_system = data.sizeSystem;
        updatePayload.sizes = data.sizes;
        updatePayload.condition = data.condition;
        updatePayload.material = data.material || null;
        updatePayload.model = data.model || null;
        updatePayload.sku = data.sku || null;
        updatePayload.weight = data.weight || null;
        updatePayload.dimensions = data.dimensions || null;
        updatePayload.delivery_option_ids = selectedDeliveryOptionIds;
      }

      const { error: updateError } = await supabase
        .from('products')
        .update(updatePayload)
        .eq('id', id)
        .eq('seller_id', user.id);

      if (updateError) {
        console.error('Error updating product:', updateError);
        throw new Error(`Failed to update product: ${updateError.message}`);
      }

      // Invalidate cache for this product since it was updated
      if (id) {
        ProductDetailService.invalidateCache(id);
        console.log('Product updated successfully, cache invalidated');
      }

      setShowSuccessModal(true);
      // Clear persisted local state on success
      try {
        if (formKey) localStorage.removeItem(formKey);
        if (stepKey) localStorage.removeItem(stepKey);
        if (shippingKey) localStorage.removeItem(shippingKey);
      } catch {}
      
      // Auto-close success modal after 3 seconds and navigate
      setTimeout(() => {
        setShowSuccessModal(false);
        handleBackNavigation();
      }, 3000);
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update product. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle image removal
  const handleRemoveImage = (index: number) => {
    const currentImages = watchedImages || [];
    const url = currentImages[index];
    // Attempt to delete from Backblaze if this image matches a B2 object
    if (b2Enabled && url) {
      try {
        const noQuery = url.split('?')[0];
        const match = b2Items.find((it) => {
          const itNoQuery = (it.url || '').split('?')[0];
          if (it.url === url || itNoQuery === noQuery) return true;
          const encKey = encodeURIComponent(it.key).replace(/%2F/g, '/');
          return noQuery.endsWith('/' + it.key) || noQuery.endsWith('/' + encKey);
        });
        if (match) void deleteBackblazeImage(match.key);
      } catch {}
    }
    const newImages = currentImages.filter((_, i) => i !== index);
    setValue('images', newImages);
  };

  const deleteBackblazeImage = async (key: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const res = await fetch('/api/b2-delete.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ key }),
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) throw new Error(json?.error || raw || 'Failed');
      setB2Items((prev) => prev.filter((it) => it.key !== key));
      const imgs = (watch('images') || []) as string[];
      const filtered = imgs.filter((u: string) => !u.includes(encodeURIComponent(key)) && !u.endsWith('/' + key));
      setValue('images', filtered);
      toast.success('Deleted');
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed');
    }
  };

  // Handle opening gallery
  const handleOpenGallery = (index: number) => {
    setGalleryIndex(index);
    setGalleryOpen(true);
  };

  // Handle gallery index change
  const handleGalleryIndexChange = (index: number) => {
    setGalleryIndex(index);
  };

  // Handle adding custom color
  const handleAddCustomColor = () => {
    const currentColors = watchedCustomColors || [];
    if (!currentColors.includes(currentCustomColor)) {
      setValue('customColors', [...currentColors, currentCustomColor]);
    }
    setColorPickerOpen(false);
  };

  // Handle removing custom color
  const handleRemoveCustomColor = (colorToRemove: string) => {
    const currentColors = watchedCustomColors || [];
    setValue('customColors', currentColors.filter(color => color !== colorToRemove));
  };

  // Handle removing standard color
  const handleRemoveStandardColor = (colorToRemove: string) => {
    const currentColors = watch('colors') || [];
    setValue('colors', currentColors.filter(color => color !== colorToRemove));
  };

  // Handle adding image from URL
  const handleAddImage = () => {
    if (imageInput.trim()) {
      const currentImages = watchedImages || [];
      setValue('images', [...currentImages, imageInput.trim()]);
      setImageInput('');
    }
  };

  // Handle uploading from device
  const handleUploadFromDevice = () => {
    fileInputRef?.click();
  };

  const handleDocumentSelect = (document: any) => {
    // Check if the document is an image
    if (document.file_type && document.file_type.startsWith('image/')) {
      // Add the document URL to the images array
      const currentImages = watchedImages || [];
      if (document.url && !currentImages.includes(document.url)) {
        setValue('images', [...currentImages, document.url]);
        toast.success(t('editProduct.toast.imageAddedFromDocs'));
      } else {
        toast.error(t('editProduct.toast.imageAlreadyAdded'));
      }
    } else {
      toast.error(t('editProduct.toast.selectImageFile'));
    }
    setDocumentsModalOpen(false);
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      Array.from(files).forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            setCropImage(e.target.result as string);
            setCropModalOpen(true);
            imageProcessedRef.current = false;
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // Camera capture functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      setVideoStream(stream);
      if (videoRef) {
        videoRef.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error(t('editProduct.toast.cameraAccessError'));
      setCameraModalOpen(false);
    }
  };

  const stopCamera = () => {
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
      if (videoRef) {
        videoRef.srcObject = null;
      }
    }
  };

  const switchCamera = async () => {
    stopCamera();
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    // Will restart camera in useEffect when facingMode changes
  };

  const capturePhoto = () => {
    if (!videoRef) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.videoWidth;
    canvas.height = videoRef.videoHeight;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Flip the image if using front camera
      if (facingMode === 'user') {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      
      ctx.drawImage(videoRef, 0, 0);
      
      canvas.toBlob((blob) => {
        if (blob) {
          // Convert blob to file
          const file = new File([blob], `camera-photo-${Date.now()}.jpg`, {
            type: 'image/jpeg'
          });
          
          // Process the captured image like a regular file upload
          const reader = new FileReader();
          reader.onload = (e) => {
            const dataURL = e.target?.result as string;
            if (dataURL) {
              setCropImage(dataURL);
              setCrop(undefined);
              setCompletedCrop(undefined);
              setScale(1);
              setRotate(0);
              imageProcessedRef.current = false;
              stopCamera();
              setCameraModalOpen(false);
              setCropModalOpen(true);
            }
          };
          reader.readAsDataURL(file);
        }
      }, 'image/jpeg', 0.9);
    }
  };

  // Start/restart camera when modal opens or facing mode changes
  useEffect(() => {
    if (cameraModalOpen) {
      startCamera();
    } else {
      stopCamera();
    }
    
    // Cleanup on unmount or when modal closes
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraModalOpen, facingMode]);

  // Update video element when ref or stream changes
  useEffect(() => {
    if (videoRef && videoStream && cameraModalOpen) {
      videoRef.srcObject = videoStream;
      videoRef.play().catch(error => {
        console.error('Error playing video:', error);
      });
    }
  }, [videoRef, videoStream, cameraModalOpen]);

  // Handle image load for cropping
  const onImageLoad = useCallback((img: HTMLImageElement) => {
    if (!img) return;
    setImgRef(img);
    const { width, height } = img;
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        1, // Free aspect ratio for product images
        width,
        height
      ),
      width,
      height
    );
    setCrop(crop);
  }, []);

  // Handle image load with ref
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img && img.complete && !imageProcessedRef.current) {
      imageProcessedRef.current = true;
      onImageLoad(img);
    }
  }, [onImageLoad]);

  // Apply crop and upload to storage (Backblaze if enabled, else Supabase)
  const applyCrop = async () => {
    if (!imgRef || !completedCrop) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const scaleX = imgRef.naturalWidth / imgRef.width;
    const scaleY = imgRef.naturalHeight / imgRef.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const sourceX = completedCrop.x * scaleX;
    const sourceY = completedCrop.y * scaleY;
    const sourceWidth = completedCrop.width * scaleX;
    const sourceHeight = completedCrop.height * scaleY;

    ctx.drawImage(
      imgRef,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          setIsUploadingImage(true);
          
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            toast.error(t('editProduct.toast.loginToUpload'));
            setIsUploadingImage(false);
            return;
          }
          const file = new File([blob], `cropped-image-${Date.now()}.jpg`, {
            type: 'image/jpeg'
          });
          if (b2Enabled) {
            // Upload to Backblaze via presigned PUT
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
              toast.error('Session missing');
              setIsUploadingImage(false);
              return;
            }
            const objectKey = `product-images/${user.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.jpg`;
            const presignRes = await fetch('/api/b2-presign.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
              body: JSON.stringify({ objectKey, contentType: 'image/jpeg', expiresIn: 900 }),
            });
            const txt = await presignRes.text();
            let presigned: any = null; try { presigned = txt ? JSON.parse(txt) : null; } catch {}
            if (!presignRes.ok || !presigned?.url) {
              console.error('Presign failed:', txt);
              toast.error(t('editProduct.toast.uploadCroppedFailed'));
              setIsUploadingImage(false);
              return;
            }
            const putRes = await fetch(presigned.url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: file });
            if (!putRes.ok) {
              toast.error(t('editProduct.toast.uploadCroppedFailed'));
              setIsUploadingImage(false);
              return;
            }
            const publicBase: string = presigned.publicBaseUrl;
            const displayUrl = `${publicBase.replace(/\/$/, '')}/${encodeURIComponent(objectKey).replace(/%2F/g,'/')}`;
            const currentImages = watchedImages || [];
            setValue('images', [...currentImages, displayUrl]);
            // Track in local B2 list for future deletes
            setB2Items((prev) => [{ key: objectKey, size: file.size, lastModified: new Date().toISOString(), url: displayUrl }, ...prev]);
          } else {
            const fileName = `${user.id}/${Date.now()}_${Math.random().toString(36).substring(2)}.jpg`;
            const { data, error } = await supabase.storage
              .from('product-images')
              .upload(fileName, file, {
                cacheControl: '3600',
                upsert: false
              });
            if (error) {
              console.error('Error uploading cropped image:', error);
              toast.error(t('editProduct.toast.uploadCroppedFailed'));
              setIsUploadingImage(false);
              return;
            }
            const { data: urlData } = supabase.storage
              .from('product-images')
              .getPublicUrl(fileName);
            const currentImages = watchedImages || [];
            setValue('images', [...currentImages, urlData.publicUrl]);
          }
          setCropModalOpen(false);
          setIsUploadingImage(false);
          toast.success(t('editProduct.toast.croppedUploaded'));
        } catch (error) {
          console.error('Error in applyCrop:', error);
          toast.error(t('editProduct.toast.processCroppedFailed'));
          setIsUploadingImage(false);
        }
      }
    }, 'image/jpeg', 0.9);
  };

  // Get contrast color for custom color chips
  const getContrastColor = (hexColor: string) => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  // Handle next step
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle step click
  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              {t('editProduct.loading.productData')}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {error}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('editProduct.errorPage.subtitle')}
            </Typography>
            <Button
              variant="contained"
              onClick={handleBackNavigation}
              startIcon={<BackIcon />}
            >
              {t('editProduct.buttons.back')}
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBackNavigation}
            variant="outlined"
          >
            {t('editProduct.buttons.back')}
          </Button>
          <Typography variant="h4" fontWeight="bold">
            {isEditingJob ? 'Job bearbeiten' : t('editProduct.title')}
          </Typography>
        </Box>

        {/* Stepper */}
        <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1 }}>
          <CardContent>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    onClick={() => handleStepClick(index)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* Form Content */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Step 1: Basic Information */}
          {activeStep === 0 && (
            <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                  {isEditingJob ? 'Grundinformationen' : t('editProduct.form.section.basic')}
                </Typography>
                
                {/* Name */}
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: isEditingJob ? 'Jobtitel ist erforderlich' : 'Product name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={isEditingJob ? 'Jobtitel' : t('editProduct.form.productName')}
                      fullWidth
                      margin="normal"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      placeholder={isEditingJob ? 'z.B. UI/UX Designer' : t('editProduct.form.productName.placeholder')}
                    />
                  )}
                />

                {/* Description */}
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: isEditingJob ? 'Jobbeschreibung ist erforderlich' : 'Product description is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={isEditingJob ? 'Jobbeschreibung' : t('editProduct.form.productDescription')}
                      fullWidth
                      multiline
                      rows={4}
                      margin="normal"
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      placeholder={isEditingJob ? 'Beschreibe Rolle, Anforderungen und Benefits…' : t('editProduct.form.productDescription.placeholder')}
                    />
                  )}
                />

                {/* Price / Salary */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                  {/* Pricing Mode Toggle */}
                  {!isEditingJob && platformFeeSettings && (
                    <Card sx={{ 
                      p: 3, 
                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                      borderRadius: 2
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">
                          Pricing Mode
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={pricingMode === 'customer'}
                              onChange={(e) => {
                                const newMode = e.target.checked ? 'customer' : 'base';
                                setPricingMode(newMode);
                                // When switching modes, recalculate the price
                                if (platformFeeSettings && watchedPrice > 0) {
                                  if (newMode === 'customer') {
                                    // Switching to customer mode - current price is base, calculate customer price
                                    const customerPrice = calculatePriceWithFeesSync(watchedPrice, platformFeeSettings);
                                    setValue('price', customerPrice);
                                  } else {
                                    // Switching to base mode - current price is customer, calculate base price
                                    const basePrice = calculateBasePriceFromCustomerPrice(watchedPrice, platformFeeSettings);
                                    setValue('price', basePrice);
                                  }
                                }
                              }}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {pricingMode === 'customer' ? 'Set Customer Price' : 'Set Base Price'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                {pricingMode === 'customer' 
                                  ? 'Enter the price customers will see' 
                                  : 'Enter your base selling price'}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>

                      {/* Fee Information Display */}
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'rgba(255, 255, 255, 0.7)', 
                        borderRadius: 2,
                        border: '1px solid rgba(102, 126, 234, 0.1)'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="body2" fontWeight="bold" color="text.primary">
                            Platform Fee:
                          </Typography>
                          {platformFeeSettings.fee_type === 'fixed' ? (
                            <Chip 
                              label={`$${platformFeeSettings.fee_amount?.toFixed(2) || '0.00'} fixed per sale`}
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 600 }}
                            />
                          ) : (
                            <Chip 
                              label={`${platformFeeSettings.fee_percentage?.toFixed(2) || '0.00'}% of sale price`}
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Box>
                        {watchedPrice > 0 && platformFeeSettings && (
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                            {pricingMode === 'customer' ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Customer Price:
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                                    ${watchedPrice.toFixed(2)}
                                  </Typography>
                                </Box>
                                {(() => {
                                  const basePrice = calculateBasePriceFromCustomerPrice(watchedPrice, platformFeeSettings);
                                  const feeAmount = watchedPrice - basePrice;
                                  return (
                                    <>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                          Platform Fee:
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold" color="error.main">
                                          -${feeAmount.toFixed(2)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                                        <Typography variant="body2" fontWeight="bold" color="text.primary">
                                          You Will Receive:
                                        </Typography>
                                        <Typography variant="h6" fontWeight="bold" color="success.main">
                                          ${basePrice.toFixed(2)}
                                        </Typography>
                                      </Box>
                                    </>
                                  );
                                })()}
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Base Price:
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                                    ${watchedPrice.toFixed(2)}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Platform Fee:
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold" color="error.main">
                                    +${calculatePlatformFeeSync(watchedPrice, platformFeeSettings).toFixed(2)}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                                  <Typography variant="body2" fontWeight="bold" color="text.primary">
                                    Customer Will Pay:
                                  </Typography>
                                  <Typography variant="h6" fontWeight="bold" color="success.main">
                                    ${calculatePriceWithFeesSync(watchedPrice, platformFeeSettings).toFixed(2)}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Card>
                  )}

                  {isEditingJob ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <MoneyIcon color="action" />
                          <Typography variant="subtitle1" fontWeight={800}>
                            Gehalt anzeigen
                          </Typography>
                        </Box>
                        <Controller
                          name="showSalary"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              checked={!!field.value}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                field.onChange(checked);
                                if (!checked) {
                                  setValue('salaryType', 'negotiable');
                                  setValue('price', 0);
                                } else {
                                  if (!watch('salaryType') || watch('salaryType') === 'negotiable') setValue('salaryType', 'hourly');
                                }
                              }}
                              icon={<HiddenIcon />}
                              checkedIcon={<VisibleIcon />}
                            />
                          )}
                        />
                      </Box>

                      {(watchedShowSalary ?? true) && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Controller
                            name="price"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Gehalt *"
                                type="number"
                                fullWidth
                                error={!!errors.price}
                                helperText={errors.price?.message || 'Der Betrag wird als Gehalt angezeigt.'}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                              />
                            )}
                          />

                          <Controller
                            name="salaryType"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <FormControl fullWidth>
                                <InputLabel>Gehaltstyp</InputLabel>
                                <Select {...field} label="Gehaltstyp">
                                  <MenuItem value="hourly">Hourly</MenuItem>
                                  <MenuItem value="fixed">Fixed</MenuItem>
                                  <MenuItem value="negotiable">Negotiable</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                          />
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Controller
                        name="price"
                        control={control}
                        rules={{ required: 'Price is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={pricingMode === 'customer' ? "Customer Price *" : "Base Price *"}
                            type="number"
                            fullWidth
                            error={!!errors.price}
                            helperText={
                              errors.price?.message ||
                              (pricingMode === 'customer'
                                ? "The price customers will see (includes platform fees)"
                                : "Your selling price (platform fees will be added)")
                            }
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              field.onChange(e);

                              // Auto-update calculations when price changes
                              if (platformFeeSettings && pricingMode === 'customer' && value > 0) {
                                // When in customer mode, validate the price is high enough
                                const calculatedBase = calculateBasePriceFromCustomerPrice(value, platformFeeSettings);
                                if (calculatedBase <= 0) {
                                  // Price too low to cover fees
                                  toast.error(
                                    platformFeeSettings.fee_type === 'fixed'
                                      ? `Price must be at least $${(platformFeeSettings.fee_amount || 0).toFixed(2)} to cover platform fees`
                                      : 'Price must be high enough to cover platform fees'
                                  );
                                }
                              }
                            }}
                          />
                        )}
                      />

                      <Controller
                        name="originalPrice"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Original Price (Optional)"
                            type="number"
                            fullWidth
                            helperText="For showing discounts"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || undefined;
                              // If original price is set and pricing mode is customer, adjust it too
                              if (value && platformFeeSettings && pricingMode === 'customer') {
                                // Calculate what the original base price would be
                                const originalBasePrice = calculateBasePriceFromCustomerPrice(value, platformFeeSettings);
                                field.onChange(originalBasePrice > 0 ? value : undefined);
                              } else {
                                field.onChange(e);
                              }
                            }}
                          />
                        )}
                      />
                    </Box>
                  )}

                  {/* Quick Price Suggestions */}
                  {platformFeeSettings && pricingMode === 'customer' && (
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'rgba(255, 248, 220, 0.3)', 
                      borderRadius: 2,
                      border: '1px solid rgba(255, 193, 7, 0.3)'
                    }}>
                      <Typography variant="body2" fontWeight="bold" color="text.primary" gutterBottom>
                        💡 Quick Price Suggestions
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {[25, 50, 75, 100, 150, 200].map((targetPrice) => {
                          const basePrice = calculateBasePriceFromCustomerPrice(targetPrice, platformFeeSettings);
                          if (basePrice <= 0) return null;
                          return (
                            <Chip
                              key={targetPrice}
                              label={`$${targetPrice.toFixed(2)} → You get $${basePrice.toFixed(2)}`}
                              onClick={() => {
                                setValue('price', targetPrice);
                              }}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'primary.main',
                                  color: 'white'
                                }
                              }}
                              variant="outlined"
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Category Selection */}
                {isEditingJob ? (
                  <Box sx={{ mt: 3 }}>
                    <Controller
                      name="industries"
                      control={control}
                      rules={{ validate: (v) => (Array.isArray(v) && v.length > 0) || 'Bitte wähle mindestens eine Kategorie.' }}
                      render={() => (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="h6" fontWeight="bold">
                              Kategorien (Job Navigation)
                            </Typography>
                            <Button
                              variant="outlined"
                              startIcon={<CategoryIcon />}
                              onClick={() => setIndustriesDialogOpen(true)}
                              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                            >
                              Kategorien auswählen
                            </Button>
                          </Box>

                          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {watchedIndustries && watchedIndustries.length > 0 ? (
                              watchedIndustries.map((label) => (
                                <Chip
                                  key={label}
                                  label={label}
                                  onDelete={() => setValue('industries', (watchedIndustries || []).filter((x) => x !== label))}
                                  sx={{ fontWeight: 700 }}
                                />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Bitte wähle mindestens eine Kategorie.
                              </Typography>
                            )}
                          </Box>

                          {errors.industries && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                              {String(errors.industries.message || 'Bitte wähle mindestens eine Kategorie.')}
                            </Typography>
                          )}
                        </Box>
                      )}
                    />
                    {renderIndustriesDialog()}
                  </Box>
                ) : (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {t('editProduct.form.category')}
                    </Typography>
                    {loadingCategories ? (
                      <Skeleton variant="rectangular" height={56} />
                    ) : categoriesError ? (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {categoriesError}
                        <Button
                          size="small"
                          onClick={() => {
                            CategoryService.getProductCategories().then(setCategories).catch(() => {
                              setCategoriesError(t('editProduct.categories.loadingFailed'));
                            });
                          }}
                          sx={{ ml: 2 }}
                        >
                          {t('editProduct.form.retry')}
                        </Button>
                      </Alert>
                    ) : (
                      <FormControl fullWidth margin="normal">
                        <InputLabel>{t('editProduct.form.selectCategory')}</InputLabel>
                        <Controller
                          name="category"
                          control={control}
                          rules={{ required: t('editProduct.validation.categoryRequired') }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              label={t('editProduct.form.selectCategory')}
                              error={!!errors.category}
                            >
                              {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CategoryIcon fontSize="small" />
                                    {category.name}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        {errors.category && (
                          <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                            {String(errors.category.message)}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  </Box>
                )}

                {/* Subcategory Selection */}
                {watchedCategory && !isEditingJob && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {t('editProduct.form.subcategory')}
                    </Typography>
                    {loadingSubcategories ? (
                      <Skeleton variant="rectangular" height={56} />
                    ) : subcategoriesError ? (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {subcategoriesError}
                        <Button size="small" onClick={() => {}} sx={{ ml: 2 }}>
                          {t('editProduct.form.retry')}
                        </Button>
                      </Alert>
                    ) : subcategories.length === 0 ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {t('editProduct.form.noSubcategories')}
                      </Alert>
                    ) : (
                      <FormControl fullWidth margin="normal">
                        <InputLabel>{t('editProduct.form.selectSubcategory')}</InputLabel>
                        <Controller
                          name="subcategory"
                          control={control}
                          rules={{ required: t('editProduct.validation.subcategoryRequired') }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              label={t('editProduct.form.selectSubcategory')}
                              error={!!errors.subcategory}
                            >
                              {subcategories.map((subcategory) => (
                                <MenuItem key={subcategory.id} value={subcategory.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SubcategoryIcon fontSize="small" />
                                    {subcategory.name}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        {errors.subcategory && (
                          <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                            {String(errors.subcategory.message)}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 2: Details & Images */}
          {activeStep === 1 && (
            <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                  {isEditingJob ? 'Details' : t('editProduct.form.section.detailsImages')}
                </Typography>

                {isEditingJob ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Controller
                        name="jobType"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <FormControl fullWidth>
                            <InputLabel>Job Type</InputLabel>
                            <Select {...field} label="Job Type">
                              <MenuItem value="full-time">Full-time</MenuItem>
                              <MenuItem value="part-time">Part-time</MenuItem>
                              <MenuItem value="contract">Contract</MenuItem>
                              <MenuItem value="freelance">Freelance</MenuItem>
                              <MenuItem value="internship">Internship</MenuItem>
                              <MenuItem value="temporary">Temporary</MenuItem>
                              <MenuItem value="volunteer">Volunteer</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Controller
                        name="experienceLevel"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <FormControl fullWidth>
                            <InputLabel>Experience Level</InputLabel>
                            <Select {...field} label="Experience Level">
                              <MenuItem value="entry-level">Entry level</MenuItem>
                              <MenuItem value="mid-level">Mid level</MenuItem>
                              <MenuItem value="senior-level">Senior level</MenuItem>
                              <MenuItem value="executive">Executive</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />

                      <Controller
                        name="duration"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Duration"
                            fullWidth
                            placeholder="e.g., 6 months, ongoing"
                          />
                        )}
                      />
                    </Box>

                    <Controller
                      name="location"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Location"
                          fullWidth
                          placeholder="e.g., Berlin, Remote, Hybrid"
                        />
                      )}
                    />

                    <Controller
                      name="remoteWorkAllowed"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                          label="Remote work allowed"
                        />
                      )}
                    />

                    <Controller
                      name="requiredSkills"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          multiple
                          freeSolo
                          options={[]}
                          value={field.value || []}
                          onChange={(event, newValue) => field.onChange(newValue)}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip {...getTagProps({ index })} key={String(option)} label={String(option)} size="small" />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Required Skills"
                              placeholder="Type and press enter"
                            />
                          )}
                        />
                      )}
                    />

                    <Controller
                      name="jobRequirements"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          multiple
                          freeSolo
                          options={[]}
                          value={field.value || []}
                          onChange={(event, newValue) => field.onChange(newValue)}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip {...getTagProps({ index })} key={String(option)} label={String(option)} size="small" />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Job Requirements"
                              placeholder="Type and press enter"
                            />
                          )}
                        />
                      )}
                    />

                    <Controller
                      name="benefitsPerks"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          multiple
                          freeSolo
                          options={[]}
                          value={field.value || []}
                          onChange={(event, newValue) => field.onChange(newValue)}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip {...getTagProps({ index })} key={String(option)} label={String(option)} size="small" />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Benefits & Perks"
                              placeholder="Type and press enter"
                            />
                          )}
                        />
                      )}
                    />

                    <Controller
                      name="contactEmail"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Contact Email"
                          fullWidth
                          type="email"
                          placeholder="applications@company.com"
                        />
                      )}
                    />

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Controller
                        name="applicationDeadline"
                        control={control}
                        render={({ field }) => (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                              label="Application Deadline"
                              value={field.value ? dayjs(field.value) : null}
                              onChange={(newValue: Dayjs | null) => {
                                field.onChange(newValue ? newValue.toISOString() : '');
                              }}
                              minDateTime={dayjs().add(1, 'hour')}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: 'white',
                                      '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#f59e0b',
                                      },
                                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#f59e0b',
                                        borderWidth: 2,
                                      },
                                      '& input': {
                                        color: '#92400e',
                                        fontWeight: 500,
                                      },
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: '#92400e',
                                      fontWeight: 600,
                                      '&.Mui-focused': {
                                        color: '#f59e0b',
                                      },
                                    },
                                    '& .MuiFormHelperText-root': {
                                      color: '#92400e',
                                      opacity: 0.8,
                                    },
                                  },
                                },
                                popper: {
                                  sx: {
                                    '& .MuiPaper-root': {
                                      borderRadius: 2,
                                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                                      border: '1px solid #f59e0b',
                                    },
                                    '& .MuiPickersCalendarHeader-root': {
                                      backgroundColor: '#fef3c7',
                                      '& .MuiIconButton-root': {
                                        color: '#f59e0b',
                                        '&:hover': {
                                          backgroundColor: '#fde68a',
                                        },
                                      },
                                      '& .MuiPickersCalendarHeader-label': {
                                        color: '#92400e',
                                        fontWeight: 600,
                                      },
                                    },
                                    '& .MuiDayCalendar-root': {
                                      '& .MuiPickersDay-root': {
                                        '&:hover': {
                                          backgroundColor: '#fef3c7',
                                        },
                                        '&.Mui-selected': {
                                          backgroundColor: '#f59e0b',
                                          '&:hover': {
                                            backgroundColor: '#d97706',
                                          },
                                        },
                                        '&.MuiPickersDay-today': {
                                          border: '1px solid #f59e0b',
                                          color: '#f59e0b',
                                        },
                                      },
                                    },
                                    '& .MuiTimeClock-root': {
                                      '& .MuiClock-root': {
                                        '& .MuiClock-meridiemText': {
                                          color: '#92400e',
                                        },
                                        '& .MuiClockNumber-root': {
                                          color: '#92400e',
                                          '&.Mui-selected': {
                                            backgroundColor: '#f59e0b',
                                            color: 'white',
                                          },
                                        },
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        )}
                      />
                      <Controller
                        name="expectedStartDate"
                        control={control}
                        render={({ field }) => (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                              label="Expected Start Date"
                              value={field.value ? dayjs(field.value) : null}
                              onChange={(newValue: Dayjs | null) => {
                                field.onChange(newValue ? newValue.toISOString() : '');
                              }}
                              minDateTime={dayjs()}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: 'white',
                                      '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#f59e0b',
                                      },
                                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#f59e0b',
                                        borderWidth: 2,
                                      },
                                      '& input': {
                                        color: '#92400e',
                                        fontWeight: 500,
                                      },
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: '#92400e',
                                      fontWeight: 600,
                                      '&.Mui-focused': {
                                        color: '#f59e0b',
                                      },
                                    },
                                    '& .MuiFormHelperText-root': {
                                      color: '#92400e',
                                      opacity: 0.8,
                                    },
                                  },
                                },
                                popper: {
                                  sx: {
                                    '& .MuiPaper-root': {
                                      borderRadius: 2,
                                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                                      border: '1px solid #f59e0b',
                                    },
                                    '& .MuiPickersCalendarHeader-root': {
                                      backgroundColor: '#fef3c7',
                                      '& .MuiIconButton-root': {
                                        color: '#f59e0b',
                                        '&:hover': {
                                          backgroundColor: '#fde68a',
                                        },
                                      },
                                      '& .MuiPickersCalendarHeader-label': {
                                        color: '#92400e',
                                        fontWeight: 600,
                                      },
                                    },
                                    '& .MuiDayCalendar-root': {
                                      '& .MuiPickersDay-root': {
                                        '&:hover': {
                                          backgroundColor: '#fef3c7',
                                        },
                                        '&.Mui-selected': {
                                          backgroundColor: '#f59e0b',
                                          '&:hover': {
                                            backgroundColor: '#d97706',
                                          },
                                        },
                                        '&.MuiPickersDay-today': {
                                          border: '1px solid #f59e0b',
                                          color: '#f59e0b',
                                        },
                                      },
                                    },
                                    '& .MuiTimeClock-root': {
                                      '& .MuiClock-root': {
                                        '& .MuiClock-meridiemText': {
                                          color: '#92400e',
                                        },
                                        '& .MuiClockNumber-root': {
                                          color: '#92400e',
                                          '&.Mui-selected': {
                                            backgroundColor: '#f59e0b',
                                            color: 'white',
                                          },
                                        },
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        )}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box>

                {/* Colors Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {t('editProduct.form.colors')}
                  </Typography>
                  
                  {/* Standard Colors */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('editProduct.form.colors.standard')}
                    </Typography>
                    <Controller
                      name="colors"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>{t('editProduct.form.colors')}</InputLabel>
                          <Select
                            {...field}
                            multiple
                            label="Select Colors"
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip key={value} label={value} size="small" />
                                ))}
                              </Box>
                            )}
                          >
                            {['Black', 'White', 'Red', 'Blue', 'Green', 'Yellow', 'Orange', 'Purple', 'Pink', 'Brown', 'Gray', 'Navy'].map((color) => (
                              <MenuItem key={color} value={color}>
                                {color}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Box>

                  {/* Custom Colors */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('editProduct.form.colors.custom')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {watchedCustomColors.map((color) => (
                        <Chip
                          key={color}
                          label={color}
                          onDelete={() => handleRemoveCustomColor(color)}
                          sx={{
                            backgroundColor: color,
                            color: getContrastColor(color),
                            '& .MuiChip-deleteIcon': {
                              color: getContrastColor(color),
                            },
                          }}
                          variant="filled"
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={() => setColorPickerOpen(true)}
                        startIcon={<AddIcon />}
                      >
                        {t('editProduct.form.colors.addCustom')}
                      </Button>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: currentCustomColor,
                          border: '2px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          cursor: 'pointer',
                        }}
                        onClick={() => setColorPickerOpen(true)}
                      />
                    </Box>
                  </Box>

                  {/* Color Picker Popup */}
                  {colorPickerOpen && (
                    <Box sx={{ position: 'relative', zIndex: 1300 }}>
                      <Box
                        sx={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          zIndex: 1300,
                        }}
                        onClick={() => setColorPickerOpen(false)}
                      />
                      <Box
                        sx={{
                          position: 'fixed',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 1301,
                        }}
                      >
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            {t('editProduct.form.colors.selectCustom')}
                          </Typography>
                          <ChromePicker
                            color={currentCustomColor}
                            onChange={(color) => setCurrentCustomColor(color.hex)}
                          />
                          <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
                            <Button
                              variant="outlined"
                              onClick={() => setColorPickerOpen(false)}
                            >
                              {t('editProduct.form.cancel')}
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleAddCustomColor}
                            >
                              {t('editProduct.form.addColor')}
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Size System */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {t('editProduct.form.sizeSystem')}
                  </Typography>
                  <Controller
                    name="sizeSystem"
                    control={control}
                    rules={{ required: 'Size system is required' }}
                    render={({ field }) => (
                      <FormControl fullWidth margin="normal">
                        <InputLabel>{t('editProduct.form.selectSizeSystem')}</InputLabel>
                        <Select
                          {...field}
                          label="Select Size System"
                          error={!!errors.sizeSystem}
                          onChange={(e) => {
                            field.onChange(e);
                            setValue('sizes', []); // Reset sizes when system changes
                          }}
                        >
                          {Object.keys(sizeSystems).map((system) => (
                            <MenuItem key={system} value={system}>
                              {system.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>

                {/* Sizes */}
                {watchedSizeSystem && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Available Sizes
                    </Typography>
                    <Controller
                      name="sizes"
                      control={control}
                      rules={{ required: 'At least one size is required' }}
                      render={({ field }) => (
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Select Sizes</InputLabel>
                          <Select
                            {...field}
                            multiple
                            label="Select Sizes"
                            error={!!errors.sizes}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip key={value} label={value} size="small" />
                                ))}
                              </Box>
                            )}
                          >
                            {sizeSystems[watchedSizeSystem]?.map((size) => (
                              <MenuItem key={size} value={size}>
                                {size}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Box>
                )}

                {/* Additional Details */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Additional Details
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Controller
                      name="brand"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Brand"
                          fullWidth
                          placeholder="e.g., Nike, Apple, Samsung"
                        />
                      )}
                    />
                    <Controller
                      name="model"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Model"
                          fullWidth
                          placeholder="e.g., iPhone 14, Air Max 90"
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Controller
                      name="material"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Material"
                          fullWidth
                          placeholder="e.g., Cotton, Leather, Plastic"
                        />
                      )}
                    />
                    <Controller
                      name="condition"
                      control={control}
                      rules={{ required: 'Condition is required' }}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Condition</InputLabel>
                          <Select
                            {...field}
                            label="Condition"
                            error={!!errors.condition}
                          >
                            <MenuItem value="new">New</MenuItem>
                            <MenuItem value="like_new">Like New</MenuItem>
                            <MenuItem value="good">Good</MenuItem>
                            <MenuItem value="fair">Fair</MenuItem>
                            <MenuItem value="poor">Poor</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Controller
                      name="sku"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="SKU"
                          fullWidth
                          placeholder="Stock Keeping Unit"
                        />
                      )}
                    />
                    <Controller
                      name="weight"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Weight (kg)"
                          type="number"
                          fullWidth
                          placeholder="0.0"
                        />
                      )}
                    />
                  </Box>

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Dimensions (cm)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Controller
                      name="dimensions"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="Length"
                          type="number"
                          fullWidth
                          placeholder="0"
                          value={field.value?.length || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange({
                              ...field.value,
                              length: value ? parseFloat(value) : 0
                            });
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="dimensions"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="Width"
                          type="number"
                          fullWidth
                          placeholder="0"
                          value={field.value?.width || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange({
                              ...field.value,
                              width: value ? parseFloat(value) : 0
                            });
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="dimensions"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="Height"
                          type="number"
                          fullWidth
                          placeholder="0"
                          value={field.value?.height || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange({
                              ...field.value,
                              height: value ? parseFloat(value) : 0
                            });
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>

                {/* Images Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Images
                  </Typography>
                  
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Add high-quality images to showcase your product. You can take a photo with your camera, upload files from your device, or paste image URLs. 
                    Supported formats: JPG, PNG, GIF, WebP. Maximum file size: 5MB per image.
                  </Alert>

                  {/* Image Upload */}
                  <Paper sx={{ p: 3, border: '2px dashed', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Add Images
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Upload files or paste image URLs
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <TextField
                        label="Image URL"
                        fullWidth
                        value={imageInput}
                        onChange={(e) => setImageInput(e.target.value)}
                        placeholder="https://example.com/image.jpg"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LinkIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={handleAddImage}
                        disabled={!imageInput.trim() || isUploading}
                        startIcon={<AddIcon />}
                      >
                        Add Image
                      </Button>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}
                        startIcon={<CameraIcon />}
                        onClick={handleUploadFromDevice}
                        disabled={isUploading}
                      >
                        {isUploading ? 'Uploading...' : 'Upload from Device'}
                      </Button>
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}
                        startIcon={<CameraIcon />}
                        onClick={() => setCameraModalOpen(true)}
                        disabled={isUploading}
                        color="primary"
                      >
                        Take Photo
                      </Button>
                      <Button
                        variant="outlined"
                        fullWidth
                        sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}
                        startIcon={<DocumentIcon />}
                        onClick={() => setDocumentsModalOpen(true)}
                        disabled={isUploading}
                      >
                        Select from Documents
                      </Button>
                    </Box>

                    {/* Hidden file input */}
                    <input
                      type="file"
                      ref={setFileInputRef}
                      onChange={handleFileUpload}
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                    />
                  </Paper>

                  {/* Image Preview */}
                  {watchedImages && watchedImages.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Preview Images ({watchedImages.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {watchedImages.map((image, index) => (
                          <Box 
                            key={index} 
                            sx={{ 
                              position: 'relative',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              },
                            }}
                            onClick={() => handleOpenGallery(index)}
                          >
                            <Box
                              component="img"
                              src={image}
                              alt={`Product ${index + 1}`}
                              sx={{
                                width: 120,
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 2,
                                border: '2px solid',
                                borderColor: 'grey.300',
                              }}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: 'error.main',
                                color: 'white',
                                '&:hover': { bgcolor: 'error.dark' },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveImage(index);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0,
                                transition: 'opacity 0.2s ease',
                                borderRadius: 2,
                                '&:hover': {
                                  opacity: 1,
                                },
                              }}
                            >
                              <PreviewIcon sx={{ color: 'white', fontSize: 32 }} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 3: Shipping (products only) */}
          {!isEditingJob && activeStep === 2 && (
            <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                  Shipping Options (Germany)
                </Typography>
                {deliveryOptionsError && <Alert severity="error">{deliveryOptionsError}</Alert>}
                {deliveryOptionsLoading ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <CircularProgress size={20} />
                    <Typography>Loading delivery options…</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                    {deliveryOptions.map((opt) => {
                      const selected = selectedDeliveryOptionIds.includes(opt.id);
                      return (
                        <Paper
                          key={opt.id}
                          onClick={() => setSelectedDeliveryOptionIds((prev) => selected ? prev.filter((id) => id !== opt.id) : Array.from(new Set([...prev, opt.id])))}
                          sx={{ p: 2, border: '2px solid', borderColor: selected ? 'primary.main' : 'divider', bgcolor: selected ? 'primary.50' : 'background.paper', cursor: 'pointer', borderRadius: 2, transition: 'all .2s ease', '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' } }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">{opt.carrier} – {opt.name}</Typography>
                            <Chip size="small" label={opt.price_eur != null ? `€${opt.price_eur.toFixed(2)}` : '—'} color={selected ? 'primary' : 'default'} />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {opt.max_weight_kg ? `${opt.max_weight_kg} kg max` : 'weight limits vary'}
                          </Typography>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 4: Review (products) / Step 3: Review (jobs) */}
          {activeStep === (isEditingJob ? 2 : 3) && (
            <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                  {isEditingJob ? 'Review Your Job' : 'Review Your Product'}
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Name */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                      {isEditingJob ? 'Job Title' : 'Product Name'}
                    </Typography>
                    <Typography variant="h6">{watch('name')}</Typography>
                  </Box>

                  {/* Description */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">{watch('description')}</Typography>
                  </Box>

                  {/* Price */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                      {isEditingJob ? 'Salary' : 'Price'}
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      ${watch('price')}
                      {watch('originalPrice') && (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, textDecoration: 'line-through' }}>
                          ${watch('originalPrice')}
                        </Typography>
                      )}
                    </Typography>
                  </Box>

                  {/* Category */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body1">
                      {isEditingJob
                        ? (Array.isArray(watch('industries')) ? (watch('industries') as string[]).join(' • ') : '')
                        : `${categories.find(c => c.id === watch('category'))?.name} • ${subcategories.find(s => s.id === watch('subcategory'))?.name}`}
                    </Typography>
                  </Box>

                  {/* Colors */}
                  {!isEditingJob && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                        Colors
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {Array.isArray(watch('colors')) && watch('colors').map((color, index) => (
                          <Chip key={index} label={color} size="small" />
                        ))}
                        {Array.isArray(watch('customColors')) && watch('customColors').map((color, index) => (
                          <Chip 
                            key={index} 
                            label={color} 
                            size="small"
                            sx={{
                              backgroundColor: color,
                              color: getContrastColor(color),
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Size System */}
                  {!isEditingJob && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                        Size System
                      </Typography>
                      <Typography variant="body1">
                        {watch('sizeSystem') ? watch('sizeSystem').replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not selected'}
                      </Typography>
                    </Box>
                  )}

                  {/* Sizes */}
                  {!isEditingJob && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                        Available Sizes
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {Array.isArray(watch('sizes')) && watch('sizes').map((size, index) => (
                          <Chip key={index} label={size} size="small" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Images */}
                  {!isEditingJob && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                        Images
                      </Typography>
                      <Typography variant="body1">
                        {Array.isArray(watch('images')) ? watch('images').length : 0} image(s) uploaded
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              startIcon={<BackIcon />}
            >
              Back
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                type="submit"
                variant="contained"
                disabled={isUploading}
                startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                sx={{ px: 4 }}
              >
                {isUploading ? 'Updating...' : 'Update Product'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<NextIcon />}
                sx={{ px: 4 }}
              >
                Next
              </Button>
            )}
          </Box>
        </form>


        {/* Image Gallery */}
        <ImageGallery
          open={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          images={watchedImages || []}
          currentIndex={galleryIndex}
          onIndexChange={handleGalleryIndexChange}
          title="Product Images"
          showDownload={true}
          showRotation={true}
          showNavigation={true}
          showZoom={true}
          showFullscreen={true}
        />

        {/* Camera Capture Modal */}
        <Dialog
          open={cameraModalOpen}
          onClose={() => {
            stopCamera();
            setCameraModalOpen(false);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              height: 'auto',
              bgcolor: '#000',
            },
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            bgcolor: '#000',
            color: 'white',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CameraIcon />
              <Typography variant="h6">Take Photo</Typography>
            </Box>
            <IconButton
              onClick={() => {
                stopCamera();
                setCameraModalOpen(false);
              }}
              sx={{ color: 'white' }}
            >
              <CancelIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, bgcolor: '#000', position: 'relative' }}>
            <Box sx={{ 
              position: 'relative',
              width: '100%',
              minHeight: 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#000',
            }}>
              {videoStream ? (
                <video
                  ref={setVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
                  }}
                />
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 2,
                  py: 8,
                  color: 'white'
                }}>
                  <CircularProgress sx={{ color: 'white' }} />
                  <Typography>Starting camera...</Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 2, 
            gap: 1, 
            bgcolor: '#000',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            justifyContent: 'space-between'
          }}>
            <Button
              onClick={switchCamera}
              variant="outlined"
              startIcon={<FlipCameraIcon />}
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              disabled={!videoStream}
            >
              Switch Camera
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={() => {
                  stopCamera();
                  setCameraModalOpen(false);
                }}
                variant="outlined"
                sx={{ 
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={capturePhoto}
                variant="contained"
                startIcon={<CameraIcon />}
                disabled={!videoStream || !videoRef}
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  },
                  minWidth: 140
                }}
              >
                Capture
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Cropping Modal */}
        <Dialog
          open={cropModalOpen}
          onClose={() => setCropModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              height: 'auto',
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CropIcon />
            Crop Image
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ position: 'relative', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {cropImage && (
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={undefined} // Free aspect ratio
                  minWidth={100}
                  minHeight={100}
                >
                  <img
                    alt="Crop me"
                    src={cropImage}
                    style={{
                      transform: `scale(${scale}) rotate(${rotate}deg)`,
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      objectFit: 'contain',
                    }}
                    onLoad={handleImageLoad}
                  />
                </ReactCrop>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setScale(Math.max(0.5, scale - 0.1))}
                startIcon={<ZoomOutIcon />}
              >
                Zoom Out
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setScale(Math.min(3, scale + 0.1))}
                startIcon={<ZoomInIcon />}
              >
                Zoom In
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setRotate(rotate - 90)}
                startIcon={<RotateLeftIcon />}
              >
                Rotate Left
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setRotate(rotate + 90)}
                startIcon={<RotateRightIcon />}
              >
                Rotate Right
              </Button>
            </Box>
            <Button
              onClick={() => setCropModalOpen(false)}
              variant="outlined"
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
            <Button
              onClick={applyCrop}
              variant="contained"
              startIcon={isUploadingImage ? <CircularProgress size={20} color="inherit" /> : <CropIcon />}
              disabled={!completedCrop || isUploadingImage}
            >
              {isUploadingImage ? 'Uploading...' : 'Apply Crop'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Modal */}
        <Dialog
          open={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f8fffe 0%, #f0fdf4 100%)',
              border: '1px solid #10b981',
              boxShadow: '0 25px 50px -12px rgba(16, 185, 129, 0.25)',
            },
          }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                type: "spring", 
                stiffness: 200, 
                damping: 15,
                delay: 0.1 
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
                }}
              >
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 200, 
                    damping: 15,
                    delay: 0.3 
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 40, color: 'white' }} />
                </motion.div>
              </Box>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#065f46', 
                  mb: 2,
                  background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Product Updated Successfully!
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#6b7280', 
                  mb: 3,
                  fontSize: '1.1rem',
                }}
              >
                Your product "{watch('name')}" has been successfully updated.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowSuccessModal(false)}
                  sx={{
                    borderColor: '#10b981',
                    color: '#10b981',
                    '&:hover': {
                      borderColor: '#059669',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    },
                  }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowSuccessModal(false);
                    handleBackNavigation();
                  }}
                  sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    },
                  }}
                >
                  Continue
                </Button>
              </Box>
            </motion.div>
          </DialogContent>
        </Dialog>

        {/* Documents Modal */}
        <UserDocumentsModal
          open={documentsModalOpen}
          onClose={() => setDocumentsModalOpen(false)}
          onDocumentSelect={handleDocumentSelect}
          mode="select"
          imagesOnly={true}
        />
      </Container>
    </Box>
  );
};

export default EditProductPage;
